import { useOnboardHSAConfigAtom, useOnboardingAtom } from 'atoms';
import { useState, useEffect } from 'react';
import { useQueryClient } from 'react-query';
import { PFX_SUPPORT_URL } from '../../constants';

// COMPONENTS
import APIsCfg from './APIsCfg';
import Insights from './Insights';
import { toast } from 'react-toastify';
import LoadingSpinner from './Spinner';
import ConsumerInt from './ConsumerInt';
import AutoOptimize from './AutoOptimize';
import TooltipPopover from '../TooltipPopover';
import Competitive_int from './Competitive_int';

// TYPES
import { User } from '../../protectedRoute';
import AccountStatusSelector from './AccountStatus';
import { TransformedData } from '../../types/editPage';
import SavingsAssessment from './SavingsAssessmentHSA/SavingsAssessment';

interface EditPageProps {
  customer: TransformedData;
  loggedInUser: User | null;
  onUpdate: (customer: TransformedData) => void;
}

function EditPage({ customer, loggedInUser, onUpdate }: EditPageProps) {
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);

  const [showUpdateButton, setShowUpdateButton] = useState(false);

  const [new_business, setnew_business] = useState(customer.new_business);
  const [isNewBusinessChanged, setIsNewBusinessChanged] = useState(false);

  const [enabledPlatforms, setEnabledPlatforms] = useState(customer.platform);

  const [googleEmail, setGoogleEmail] = useState('');
  // Default tab based on new_business flag
  const [activeTab, setActiveTab] = useState(
    customer.new_business ? 'Competitive_int' : 'APIsCfg'
  );

  const [isOnboarding, setIsOnboarding] = useOnboardingAtom();

  const customerInactive = customer.status === 'Inactive';

  const isAdmin = loggedInUser?.role === 'Admin';

  // Handle platform change and show Update Onboarding button if there are changes
  useEffect(() => {
    if (
      (customer.status === 'Active' ||
        customer.status === 'Onboarding' ||
        customer.status === 'Inactive' ||
        customer.status === 'HistoricalAccess') &&
      JSON.stringify(enabledPlatforms) !== JSON.stringify(customer.platform)
    ) {
      setShowUpdateButton(true);
    } else {
      setShowUpdateButton(false);
    }
  }, [enabledPlatforms, customer.status, customer.platform]);

  const handleSaveNewBusiness = () => {
    setIsLoading(true);
    fetch('update-new-business/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ customer_id: customer.customer_id, new_business }),
    })
      .catch((error) => {
        console.error('Error saving New Business flag:', error);
      })
      .finally(() => {
        setIsLoading(false); // Reset saving state
      });
  };

  // Function to handle onboarding process
  const handleOnboarding = () => {
    setIsLoading(true);
    setIsOnboarding(true);
    fetch('start-onboarding/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        customer_id: customer.customer_id,
        enabled_platforms: enabledPlatforms,
      }),
    })
      .then((response) => {
        if (response.ok) {
          response.json().then((res) => {
            toast.success(res?.message || 'Onboarding success');
            // Platforms (and possibly the dataset) changed; refresh the sidebar.
            queryClient.invalidateQueries(['get_customer_info_sidebar']);
          });
        } else {
          response.json().then((res) => {
            toast.error(
              () => (
                <div>
                  <p className="font-bold">
                    {res.error || 'An unknown error occured.'}
                  </p>
                  <p>
                    Please raise an issue via the{' '}
                    <a
                      className="underline"
                      href={PFX_SUPPORT_URL}
                      target="_blank"
                      rel="noreferrer"
                    >
                      OneSearch support site
                    </a>{' '}
                    and include a reference to module and client .
                  </p>
                </div>
              ),
              { autoClose: 8000 }
            );
          });
        }
      })
      .catch((error) => {
        console.error('Error in fetch operation:', error);
        toast.error('An error occurred during the onboarding process.');
      })
      .finally(() => {
        setIsLoading(false);
        setIsOnboarding(false);
      });
  };

  const fields = [
    { label: 'Agency', value: customer.agency },
    { label: 'Client', value: customer.client },
    { label: 'Line of Business', value: customer.line_of_business },
    { label: 'Market', value: customer.market },
    { label: 'Region', value: customer.region },
  ];

  const moduleMap: { [key: string]: string } = {
    '1': 'Insights',
    '2': 'Competitive Intelligence',
    '3': 'Consumer Intent',
    '4': 'Auto Optimize',
  };

  const isPlatformEnabled = (platformId: string) =>
    enabledPlatforms.includes(platformId);

  const enablePlatform = (platformId: string) => {
    setEnabledPlatforms([...enabledPlatforms, platformId]);
  };

  const disablePlatform = (platformId: string) => {
    setEnabledPlatforms(enabledPlatforms.filter((id) => id !== platformId));
  };

  const gridColsClass = 'grid-cols-4'; // Always keep 4 columns

  // Separate enabled and disabled platforms
  const enabledPlatformsList = Object.keys(moduleMap).filter(isPlatformEnabled);
  const disabledPlatformsList = Object.keys(moduleMap).filter(
    (platformId) => !isPlatformEnabled(platformId)
  );

  const isReadOnly =
    isOnboarding ||
    customer.status === 'Inactive' ||
    customer.status === 'Onboarding';
  //  || customer.status === 'HistoricalAccess';

  return (
    <div className="bg-light rounded-2xl shadow-sm mt-8">
      <div className="border-b border-success mb-2">
        <div className="flex justify-between items-center">
          <div>
            {/* missing text subtitle */}
            <h2 className="text-primarygray text-2xl font-bold pl-4 py-2">
              Edit Account
            </h2>
          </div>
          {/* Conditionally render the onboarding button */}
          <button
            onClick={handleOnboarding}
            className="text-sm font-bold py-2 px-4 rounded-full items-center bg-blue800 text-light mr-4 cursor-pointer disabled:cursor-not-allowed disabled:opacity-55"
            disabled={!showUpdateButton || isReadOnly}
          >
            Start Onboarding
          </button>
        </div>
        <div className="ml-4 mb-3 pl-0 ">
          <p className="text-lg font-bold text-green-600 opacity-80 tracking-wide">
            {[customer.client, customer.line_of_business, customer.market]
              .filter(Boolean)
              .join(' - ')}
          </p>
        </div>
      </div>

      {/* Toggle for New Business */}
      {customer.new_business ? (
        <div className="flex items-center space-x-4 px-4 py-4">
          <TooltipPopover
            content="settings_page.account_management_tab.new_business"
            type="info"
            side="top"
            className="-top-2 -right-3"
          >
            <label className="font-bold text-sm">New Business:</label>
          </TooltipPopover>
          <input
            type="checkbox"
            checked={new_business}
            onChange={() => {
              setnew_business(!new_business);
              setIsNewBusinessChanged(true); // Mark as changed
              setActiveTab(new_business ? 'APIsCfg' : 'Competitive_int');
            }}
            className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <button
            onClick={handleSaveNewBusiness}
            className={`text-sm font-bold py-2 px-4 rounded-full items-center ${
              isNewBusinessChanged ? 'bg-green-600' : 'bg-gray-300'
            } text-light`}
            disabled={!isNewBusinessChanged || isLoading || isOnboarding}
          >
            {isLoading ? <LoadingSpinner /> : 'Save New Business'}
          </button>
        </div>
      ) : null}

      {/* Status Selector */}
      <AccountStatusSelector
        customer={customer}
        onUpdate={onUpdate}
        isOnboarding={isOnboarding}
        isNewBusiness={new_business}
        enabledPlatforms={enabledPlatforms}
      />

      {/* Fields Section */}
      <div className="flex flex-col space-y-4 px-4 py-4">
        {/* Display OneSearch Customer ID to Admins only */}
        {isAdmin ? (
          <div className="grid grid-cols-5 gap-4">
            <div className="flex flex-col space-y-2">
              <label className="font-bold text-sm">Onesearch Customer ID</label>
              <input
                type="text"
                className="border border-gray200 rounded p-2 text-sm text-thirdgray read-only:cursor-not-allowed"
                value={customer.customer_id}
                readOnly
              />
            </div>
          </div>
        ) : null}

        <div className="grid grid-cols-5 gap-4">
          {fields.map((field, index) => (
            <div key={index} className="flex flex-col space-y-2">
              <label className="font-bold text-sm">{field.label}</label>
              <input
                type="text"
                className="border border-gray200 rounded p-2 text-sm text-thirdgray read-only:cursor-not-allowed"
                value={field.value}
                readOnly
              />
            </div>
          ))}
        </div>

        {/* Platform Indicators */}
        {!new_business ? (
          <div className="flex flex-col space-y-2">
            <label className="font-bold text-sm">Modules</label>
            <div className={`grid ${gridColsClass} gap-6 text-sm font-bold`}>
              {/* Show enabled platforms first */}
              {enabledPlatformsList.map((platformId) => (
                <div
                  key={platformId}
                  className={`flex flex-row items-center justify-between space-x-2 bg-green-100 border-success rounded border px-4 py-2 ${
                    isReadOnly ? 'opacity-60 cursor-not-allowed' : ''
                  }`}
                  style={{
                    boxShadow:
                      '0px 2px 17px 0px rgba(0, 0, 0, 0.08), 0px 0px 1px 0px #FEFCFD',
                  }}
                >
                  <span>{moduleMap[platformId]}</span>
                  <div className="flex space-x-2 disabled:cursor-not-allowed">
                    <button
                      onClick={() => disablePlatform(platformId)}
                      title="Disable module."
                      disabled={isReadOnly}
                    >
                      <img
                        src={`${process.env.PUBLIC_URL}/static/img/pause.svg`}
                        alt="pause"
                        className="h-6 w-6"
                      />
                    </button>
                  </div>
                </div>
              ))}

              {/* Show disabled platforms at the end */}
              {disabledPlatformsList.map((platformId) => (
                <div
                  key={platformId}
                  className="flex flex-row items-center justify-between space-x-2 bg-gray-100 border-gray300 rounded border px-4 py-2"
                  style={{
                    boxShadow:
                      '0px 2px 17px 0px rgba(0, 0, 0, 0.08), 0px 0px 1px 0px #FEFCFD',
                  }}
                >
                  <span>{moduleMap[platformId]}</span>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => enablePlatform(platformId)}
                      title="Enable module"
                      disabled={isReadOnly}
                    >
                      <img
                        src={`${process.env.PUBLIC_URL}/static/img/play.svg`}
                        alt="play"
                        className="h-6 w-6"
                      />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {/* Tabs */}
        {!new_business ? (
          <div className="flex space-x-4 border-b-2 mb-4">
            {/* Always show APIsCfg tab */}
            <button
              onClick={() => setActiveTab('APIsCfg')}
              className={`text-sm font-bold px-4 py-2 ${
                activeTab === 'APIsCfg'
                  ? 'border-b-2 border-blue800 text-blue800'
                  : ''
              }`}
            >
              APIs Configuration
            </button>

            {/* Show Competitive Intelligence tab */}
            <button
              onClick={() => setActiveTab('Competitive_int')}
              className={`text-sm font-bold px-4 py-2 ${
                activeTab === 'Competitive_int'
                  ? 'border-b-2 border-blue800 text-blue800'
                  : ''
              }`}
            >
              Competitive Intelligence
            </button>

            {/* Show other tabs if New Business is False */}
            {enabledPlatformsList.includes('1') && (
              <button
                onClick={() => setActiveTab('Insights')}
                className={`text-sm font-bold px-4 py-2 ${
                  activeTab === 'Insights'
                    ? 'border-b-2 border-blue800 text-blue800'
                    : ''
                }`}
              >
                Insights
              </button>
            )}
            {enabledPlatformsList.includes('3') && (
              <button
                onClick={() => setActiveTab('ConsumerInt')}
                className={`text-sm font-bold px-4 py-2 ${
                  activeTab === 'ConsumerInt'
                    ? 'border-b-2 border-blue800 text-blue800'
                    : ''
                }`}
              >
                Consumer Intent
              </button>
            )}
            {enabledPlatformsList.includes('4') && (
              <button
                onClick={() => setActiveTab('AutoOptimize')}
                className={`text-sm font-bold px-4 py-2 ${
                  activeTab === 'AutoOptimize'
                    ? 'border-b-2 border-blue800 text-blue800'
                    : ''
                }`}
              >
                Auto Optimize
              </button>
            )}
            {/* HSA SavingsAssessment Tab */}
            {!enabledPlatformsList.includes('4') && !customerInactive && (
              <button
                onClick={() => setActiveTab('SavingsAssessment')}
                className={`text-sm font-bold px-4 py-2 ${
                  activeTab === 'SavingsAssessment'
                    ? 'border-b-2 border-blue800 text-blue800'
                    : ''
                }`}
              >
                Savings Assessment
              </button>
            )}
          </div>
        ) : null}

        <div className="relative">
          {/* Disable actions on all tabs when status is read only */}
          {isReadOnly ? (
            <div className="absolute cursor-not-allowed top-0 left-0 h-full w-full z-10"></div>
          ) : null}

          {/* Conditionally Render the Modules Based on Active Tab */}
          {/*  HSA SavingsAssessment Tab */}
          {activeTab === 'SavingsAssessment' && (
            <SavingsAssessment
              customer={customer}
              enabled={!isPlatformEnabled('4') && !customerInactive}
            />
          )}
          {activeTab === 'APIsCfg' && (
            <APIsCfg
              customerId={customer.customer_id}
              googleEmail={googleEmail}
              setGoogleEmail={setGoogleEmail}
            />
          )}
          {activeTab === 'ConsumerInt' && (
            <ConsumerInt
              customerId={customer.customer_id}
              enabled={isPlatformEnabled('3')}
              client={customer.client}
              lineOfBusiness={customer.line_of_business}
              market={customer.market}
            />
          )}
          {activeTab === 'Competitive_int' && (
            <Competitive_int
              customerId={customer.customer_id}
              enabled={isPlatformEnabled('2')}
              loggedInUser={loggedInUser}
              client={customer.client}
              lineOfBusiness={customer.line_of_business}
              market={customer.market}
            />
          )}
          {activeTab === 'AutoOptimize' && (
            <AutoOptimize
              customerId={customer.customer_id}
              enabled={isPlatformEnabled('4')}
              client={customer.client}
              lineOfBusiness={customer.line_of_business}
              market={customer.market}
            />
          )}
          {activeTab === 'Insights' && (
            <Insights
              customerId={customer.customer_id}
              enabled={isPlatformEnabled('1')}
              client={customer.client}
              lineOfBusiness={customer.line_of_business}
              market={customer.market}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default EditPage;
